import { z } from 'zod';
import { coreIds } from './ids.js';
import { CreateAgentRequestSchema } from './api.types.js';
import {
  PluginAttachmentValueSchema,
  ResourceSizeSchema,
  UserContentBlocksSchema,
} from '@runloop/reflex-contract';
import { AgentEnvVarSchema } from './agent.types.js';

/**
 * Launch-dialog form snapshot stored in `agent_drafts.payload`. Extends the
 * launch API contract with fields needed to restore the dialog reducer.
 */
export const AgentDraftPayloadSchema = CreateAgentRequestSchema.partial().extend({
  selectedProviderId: z.string().nullable().optional(),
  manualBlueprintName: z.string().nullable().optional(),
  snapshotId: z.string().nullable().optional(),
  resourceSize: ResourceSizeSchema.nullable().optional(),
  dockerd: z.boolean().optional(),
  computerUse: z.boolean().optional(),
  idleTimeMinutes: z.number().nullable().optional(),
  selectedPersonaId: z.string().nullable().optional(),
  reasoningEffort: z.string().nullable().optional(),
  initialPrompt: z.string().optional(),
  chatAttachments: z.array(z.unknown()).optional(),
  attachmentValues: z.record(z.string(), PluginAttachmentValueSchema).optional(),
  modelEndpointId: z.string().nullable().optional(),
  content: UserContentBlocksSchema.optional(),
  envVars: z.array(AgentEnvVarSchema).optional(),
});
export type AgentDraftPayload = z.infer<typeof AgentDraftPayloadSchema>;

export const AgentDraftIdSchema = coreIds.schema('agentDraft');
export type AgentDraftId = z.infer<typeof AgentDraftIdSchema>;

export const AgentDraftSchema = z.object({
  id: AgentDraftIdSchema,
  userId: coreIds.schema('user'),
  organizationId: coreIds.schema('organization'),
  payload: AgentDraftPayloadSchema,
  updatedAt: z.number(),
});
export type AgentDraft = z.infer<typeof AgentDraftSchema>;

export const UpsertAgentDraftRequestSchema = z.object({
  id: AgentDraftIdSchema.optional(),
  payload: AgentDraftPayloadSchema,
  clientSeq: z.number().int().nonnegative(),
});
export type UpsertAgentDraftRequest = z.infer<typeof UpsertAgentDraftRequestSchema>;

export const UpsertAgentDraftResponseSchema = z.object({
  draft: AgentDraftSchema,
  clientSeq: z.number().int().nonnegative(),
});
export type UpsertAgentDraftResponse = z.infer<typeof UpsertAgentDraftResponseSchema>;

export const ListAgentDraftsResponseSchema = z.object({
  drafts: z.array(AgentDraftSchema),
});
export type ListAgentDraftsResponse = z.infer<typeof ListAgentDraftsResponseSchema>;
